import { createDb } from './db'
import { isProxy, toRaw } from 'vue'

const SINGLETON_ID = 'current'

let db = null
let initPromise = null

export function getLocalDb() {
  if (!db) db = createDb()
  return db
}

export async function initLocalDb() {
  if (initPromise) return initPromise
  initPromise = (async () => {
    const instance = getLocalDb()
    await instance.open()
    return instance
  })()
  return initPromise
}

function nowMs() {
  return Date.now()
}

function deproxy(value) {
  if (value == null) return value

  // Strip Vue reactive proxies so IndexedDB can clone the payload.
  const raw = isProxy(value) ? toRaw(value) : value

  if (Array.isArray(raw)) {
    return raw.map(deproxy)
  }

  if (typeof raw === 'object') {
    const out = {}
    for (const [k, v] of Object.entries(raw)) {
      out[k] = deproxy(v)
    }
    return out
  }

  return raw
}

function cloneForIdb(value) {
  const cleaned = deproxy(value)

  // Prefer structuredClone (preserves more types), fall back to JSON clone.
  try {
    return typeof structuredClone === 'function' ? structuredClone(cleaned) : JSON.parse(JSON.stringify(cleaned))
  } catch {
    return JSON.parse(JSON.stringify(cleaned))
  }
}

export async function cacheSetUser(user) {
  const instance = await initLocalDb()
  await instance.user.put({ id: SINGLETON_ID, value: cloneForIdb(user), updatedAt: nowMs() })
}

export async function cacheGetUser() {
  const instance = await initLocalDb()
  const row = await instance.user.get(SINGLETON_ID)
  return row?.value || null
}

export async function cacheSetSession(session) {
  const instance = await initLocalDb()
  await instance.session.put({ id: SINGLETON_ID, value: cloneForIdb(session), updatedAt: nowMs() })
}

export async function cacheGetSession() {
  const instance = await initLocalDb()
  const row = await instance.session.get(SINGLETON_ID)
  return row?.value || null
}

export async function cacheSetLocations({ locations, currentLocationId }) {
  const instance = await initLocalDb()
  await instance.locations.put({
    id: SINGLETON_ID,
    value: cloneForIdb({ locations: locations || [], currentLocationId: currentLocationId || null }),
    updatedAt: nowMs()
  })
}

export async function cacheGetLocations() {
  const instance = await initLocalDb()
  const row = await instance.locations.get(SINGLETON_ID)
  return row?.value || null
}

export async function cacheSetRegisterOverview(locationId, overview) {
  const instance = await initLocalDb()
  const id = `location:${locationId}`
  await instance.registerOverview.put({ id, locationId, value: cloneForIdb(overview), updatedAt: nowMs() })
}

export async function cacheGetRegisterOverview(locationId) {
  const instance = await initLocalDb()
  const id = `location:${locationId}`
  const row = await instance.registerOverview.get(id)
  return row?.value || null
}

export async function cacheSetPosSnapshot(snapshot) {
  const instance = await initLocalDb()
  await instance.posSnapshot.put({ id: SINGLETON_ID, value: cloneForIdb(snapshot), updatedAt: nowMs() })
}

export async function cacheGetPosSnapshot() {
  const instance = await initLocalDb()
  const row = await instance.posSnapshot.get(SINGLETON_ID)
  return row?.value || null
}

export async function clearLocalDb() {
  const instance = await initLocalDb()
  await instance.transaction('rw', instance.user, instance.session, instance.locations, instance.registerOverview, instance.posSnapshot, async () => {
    await Promise.all([
      instance.user.clear(),
      instance.session.clear(),
      instance.locations.clear(),
      instance.registerOverview.clear(),
      instance.posSnapshot.clear()
    ])
  })
}

export { detectPlatform } from './platform'
